import clsx from "clsx";
import styles from "./styles.module.scss";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from "body-scroll-lock";
import type { PickerValue } from "react-mobile-picker";
import Picker from "react-mobile-picker";
import Button from "../button";

export interface DateTimeValue extends Record<string, string> {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
}

export type PickerMode = 'date' | 'datetime';

interface Props {
  onChange: (value: DateTimeValue) => void;
  value?: DateTimeValue;
  isOpen: boolean;
  onClose: () => void;
  mode?: PickerMode;
  start?: number;
  length?: number;
  calc?: "ADD" | "SUB";
}

export default function WheelDateTimePicker({
  onChange,
  value,
  isOpen,
  onClose,
  mode: initialMode = 'datetime', // デフォルトは日時選択モード
  start,
  length = 10,
  calc = "ADD",
}: Props) {
  // mode is now controlled by the prop only, no internal state
  const mode = initialMode;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentDay = new Date().getDate();
  const currentHour = new Date().getHours();
  const currentMinute = new Date().getMinutes();
  
  // start が指定されている場合はそれを使用、なければ現在の年
  const startYear = start ?? currentYear;

  const defaultValue: DateTimeValue = {
    year: String(currentYear),
    month: String(currentMonth).padStart(2, "0"),
    day: String(currentDay).padStart(2, "0"),
    hour: String(currentHour).padStart(2, "0"),
    minute: String(Math.floor(currentMinute / 30) * 30).padStart(2, "0"),
  };

  const [pickerValue, setPickerValue] = useState<DateTimeValue>(value || defaultValue);

  const handlePickerChange = useCallback(
    (newValue: PickerValue, key: string) => {
      const dateTimeValue = newValue as DateTimeValue;
      
      // 日付モードの場合、時間関連の変更は無視
      if (mode === 'date' && (key === 'hour' || key === 'minute')) {
        return;
      }
      
      if (key === "day" || key === "hour" || key === "minute") {
        setPickerValue(dateTimeValue);
        return;
      }

      const { year, month } = dateTimeValue;
      const newDayArray = getDayArray(Number(year), Number(month));
      const newDay = newDayArray.includes(dateTimeValue.day)
        ? dateTimeValue.day
        : newDayArray[newDayArray.length - 1];
      setPickerValue({ ...dateTimeValue, day: newDay });
    },
    [mode]
  );

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!modalRef.current) return;

    if (isOpen) {
      disableBodyScroll(modalRef.current);
    } else {
      enableBodyScroll(modalRef.current);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [isOpen]);

  const handleConfirm = () => {
    const resultValue = mode === 'date' 
      ? { ...pickerValue, hour: '00', minute: '00' } // 日付モードの場合は時間を00:00に設定
      : pickerValue;
    onChange(resultValue);
    onClose();
  };


  return (
    <>
      {isOpen && (
        <div className={clsx(styles.modal)} ref={modalRef}>
          <div className={clsx(styles.overlay, isOpen && styles.overlayOpen)} onClick={onClose} />
          <div className={clsx(styles.dialog, isOpen && styles.dialogOpen)}>
            <div className={styles.dialogHeader}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={onClose}
              >
                キャンセル
              </button>
              <Button
                size="sm"
                className={styles.confirmButton}
                onClick={handleConfirm}
              >
                決定
              </Button>
            </div>
            <div className={styles.dialogBody}>
              <div className={styles.picker}>
                <Picker
                  value={pickerValue}
                  onChange={handlePickerChange}
                  wheelMode="normal"
                >
                  <Picker.Column name="year">
                    {Array.from({ length }, (_, i) =>
                      calc === "SUB"
                        ? String(startYear - length + i + 1)
                        : String(startYear + i)
                    ).map((year) => (
                      <Picker.Item key={year} value={year}>
                        {({ selected }) => (
                          <div className={clsx(selected && styles.active)}>
                            {year}
                          </div>
                        )}
                      </Picker.Item>
                    ))}
                  </Picker.Column>
                  <Picker.Column name="month">
                    {Array.from({ length: 12 }, (_, i) =>
                      String(i + 1).padStart(2, "0")
                    ).map((month) => (
                      <Picker.Item key={month} value={month}>
                        {({ selected }) => (
                          <div className={clsx(selected && styles.active)}>
                            {month}
                          </div>
                        )}
                      </Picker.Item>
                    ))}
                  </Picker.Column>
                  <Picker.Column name="day">
                    {getDayArray(Number(pickerValue.year), Number(pickerValue.month)).map(
                      (day) => (
                        <Picker.Item key={day} value={day}>
                          {({ selected }) => (
                            <div className={clsx(selected && styles.active)}>
                              {day}
                            </div>
                          )}
                        </Picker.Item>
                      )
                    )}
                  </Picker.Column>
                  {mode === 'datetime' && (
                    <>
                      <Picker.Column name="hour">
                        {Array.from({ length: 24 }, (_, i) =>
                          String(i).padStart(2, "0")
                        ).map((hour) => (
                          <Picker.Item key={hour} value={hour}>
                            {({ selected }) => (
                              <div className={clsx(selected && styles.active)}>
                                {hour}
                              </div>
                            )}
                          </Picker.Item>
                        ))}
                      </Picker.Column>
                      <Picker.Column name="minute">
                        {Array.from({ length: 6 }, (_, i) => ['00', '30'][i % 2]).map((minute, index) => (
                          <Picker.Item key={`${minute}-${index}`} value={minute}>
                            {({ selected }) => (
                              <div className={clsx(selected && styles.active)}>
                                {minute}
                              </div>
                            )}
                          </Picker.Item>
                        ))}
                      </Picker.Column>
                    </>
                  )}
                </Picker>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function getDayArray(year: number, month: number) {
  const dayCount = new Date(year, month, 0).getDate();
  return Array.from({ length: dayCount }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );
}
